import { ORG_CLIMATE_SURVEY_VIEWER_UIDS } from './orgClimateSurveys/access';

export type ComplianceCategoryDataMode = 'submission' | 'import';

export interface ComplianceCategory {
  id: string;
  title: string;
  dataMode?: ComplianceCategoryDataMode;
  teamId?: string;
  teamLabel?: string;
  periodLabel?: string;
  periodicity?: string;
  color?: string;
  charts?: number;
  departmentIds?: string[];
  userIds?: string[];
  tags?: string[];
  active?: boolean;
  helpText?: string;
  severityDefault?: string;
}

export const MANDATORY_COMPLIANCE_CATEGORY_IDS = ['nurse_notes'];

export const compliancePalette = {
  primary: '#6F4CF6',
  green: '#22C55E',
  amber: '#F59E0B',
  red: '#EF4444',
  blue: '#3B82F6',
};

const DEFAULT_CHARTS = 10;

export const NURSE_NOTES_TEAM_LABEL = 'คณะทำงานพัฒนาระบบบันทึกทางการพยาบาล';
export const ETHIC_TEAM_LABEL =
  'คณะทำงานส่งเสริมจริยธรรมวิชาชีพและพฤติกรรมบริการ ฝ่ายการพยาบาล';
export const MED_ERROR_TEAM_FULL_LABEL = 'คณะทำงานความคลาดเคลื่อนทางยาฝ่ายการพยาบาล';
export const MED_ERROR_INCIDENT_LABEL = 'รายงานอุบัติการณ์ความคลาดเคลื่อนทางยา';

const CATEGORY_ID_SHAPE = /^[A-Za-z0-9_]+$/;

/** Staff-facing category title. Never returns a category id. */
export function complianceTitleForUi(idOrTitle: string | null | undefined): string {
  const raw = String(idOrTitle ?? '').trim();
  if (!raw) return 'ไม่ทราบชื่อหมวด';
  const match = DEFAULT_COMPLIANCE_CATEGORIES.find((category) => category.id === raw || category.title === raw);
  if (match?.title) return match.title;
  if (CATEGORY_ID_SHAPE.test(raw)) return 'ไม่ทราบชื่อหมวด';
  return raw;
}

export const DEFAULT_COMPLIANCE_CATEGORIES: ComplianceCategory[] = [
  {
    id: 'needleandsharp',
    title: 'เข็มตำ/ของมีคมบาด',
    dataMode: 'submission',
    teamLabel: 'คณะทำงานบริหารจัดการความเสี่ยง',
    periodicity: 'monthly',
    color: compliancePalette.amber,
    charts: DEFAULT_CHARTS,
    periodLabel: 'ครั้งที่',
    active: true,
  },
  {
    id: 'med_error',
    title: 'นิเทศระบบยา',
    dataMode: 'submission',
    teamLabel: 'คณะทำงานความคลาดเคลื่อนทางยา',
    periodicity: 'monthly',
    color: compliancePalette.blue,
    charts: 4,
    periodLabel: 'สัปดาห์',
    active: true,
  },
  {
    id: 'siren_bundle',
    title: 'SIREN bundle',
    dataMode: 'submission',
    teamLabel: 'คณะทำงาน Palliative, Pain, and Pressure Injury',
    periodicity: 'monthly',
    color: compliancePalette.primary,
    charts: 5,
    periodLabel: 'HN',
    active: true,
  },
  {
    id: 'nurse_notes',
    title: 'บันทึกทางการพยาบาล',
    dataMode: 'submission',
    teamLabel: NURSE_NOTES_TEAM_LABEL,
    periodicity: 'monthly',
    color: compliancePalette.red,
    charts: 8,
    periodLabel: 'Chart',
    active: true,
  },
  {
    id: 'ethicSurveys',
    title: 'บรรยากาศองค์กรจริยธรรม',
    dataMode: 'import',
    teamLabel: ETHIC_TEAM_LABEL,
    periodicity: 'monthly',
    color: compliancePalette.primary,
    charts: 0,
    active: true,
  },
  {
    id: 'orgClimateSurveys',
    title: 'แบบประเมินบรรยากาศจริยธรรมในองค์การพยาบาล',
    dataMode: 'import',
    periodicity: 'monthly',
    color: compliancePalette.primary,
    charts: 0,
    active: true,
    userIds: [...ORG_CLIMATE_SURVEY_VIEWER_UIDS],
    helpText:
      `แบบประเมิน 32 ข้อ สำหรับพยาบาล (จำกัดผู้ดู 3 บัญชี) — ไม่ใช่แบบสอบถาม${ETHIC_TEAM_LABEL} 52 ข้อ`,
  },
  {
    id: 'med_error_incident',
    title: MED_ERROR_INCIDENT_LABEL,
    dataMode: 'import',
    teamLabel: 'คณะทำงานความคลาดเคลื่อนทางยา',
    periodicity: 'monthly',
    color: compliancePalette.red,
    charts: 0,
    active: true,
  },
  {
    id: 'fall_incident',
    title: 'จำนวนอุบัติการณ์การพลัดตกหกล้ม',
    dataMode: 'import',
    teamLabel: 'คณะทำงานความปลอดภัย / การพลัดตกหกล้ม',
    periodicity: 'monthly',
    color: compliancePalette.amber,
    charts: 0,
    active: true,
  },
  {
    id: 'kpoa',
    title: 'คปอ.',
    dataMode: 'import',
    teamLabel: 'คปอ.',
    periodicity: 'monthly',
    color: compliancePalette.blue,
    charts: 0,
    active: true,
  },
  {
    id: 'hr',
    title: 'ทรัพยากรบุคคล',
    dataMode: 'import',
    teamLabel: 'คณะกรรมการทรัพยากรบุคคล',
    periodicity: 'monthly',
    color: compliancePalette.green,
    charts: 0,
    active: true,
  },
];

/** Submission categories: user-created/editable, stored in submissions collection. */
export function isSubmissionCategory(categoryId: string): boolean {
  const cat = DEFAULT_COMPLIANCE_CATEGORIES.find(
    (c) => c.id === categoryId || c.title === categoryId,
  );
  return cat?.dataMode !== 'import';
}

/** Import categories: data synced from external sources, read-only in app. */
export function isImportCategory(categoryId: string): boolean {
  const cat = DEFAULT_COMPLIANCE_CATEGORIES.find(
    (c) => c.id === categoryId || c.title === categoryId,
  );
  return cat?.dataMode === 'import';
}
